import { useEffect, useRef, useState, type CSSProperties } from 'react'
import { logo, REEL_SEGMENTS } from '../../assets/index.js'
import { useAudio } from '../../hooks/useAudio.js'
import {
  DEFAULT_COMMANDER_NAME,
  MAX_COMMANDER_NAME_LENGTH,
  loadCommanderName,
  saveCommanderName,
} from '../../utils/commanderName.js'

interface SplashProps {
  onPlay: () => void
}

const HOLD_S = 7
const CYCLE_S = REEL_SEGMENTS.length * HOLD_S
const CLASSIC_SEGMENT = REEL_SEGMENTS[REEL_SEGMENTS.length - 1]

const NAVY = '#0B2545'

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(
    () => window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  )
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    const onChange = () => setReduced(mq.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])
  return reduced
}

const primaryButtonStyle: CSSProperties = {
  width: '100%',
  maxWidth: 280,
  padding: '16px 24px',
  fontSize: 16,
  fontWeight: 700,
  border: 'none',
  borderRadius: 8,
  backgroundColor: '#F4B942',
  color: NAVY,
  cursor: 'pointer',
  letterSpacing: 1,
}

/** Chip style shared by the greeting and the name-entry prompt (§11.4) */
const chipStyle: CSSProperties = {
  display: 'inline-block',
  fontSize: 13,
  fontWeight: 600,
  letterSpacing: 1.5,
  color: '#EAF2F8',
  backgroundColor: 'rgba(11,37,69,0.85)',
  border: '1px solid rgba(141,169,196,0.4)',
  borderRadius: 6,
  padding: '8px 14px',
}

/**
 * Splash / main menu (GDD-v3 §2.2, ART_GUIDE_v3 §11).
 *
 * Living theater reel behind the logo + CTA. Split HUD: logo + tagline pinned
 * top, identity + CTA pinned bottom — both always visible so the logo never
 * disappears. First visit swaps the bottom group into an inline name-entry
 * stack (same chip styling as the returning greeting) instead of a modal.
 * The name persists locally and is immersion-only.
 * Flow: Splash → Theater Select → game (GDD-v3 §2.1).
 */
export function Splash({ onPlay }: SplashProps) {
  const { play } = useAudio()
  const reducedMotion = usePrefersReducedMotion()

  const [name] = useState<string | null>(() => loadCommanderName())
  const [editing, setEditing] = useState(() => loadCommanderName() === null)
  const [draft, setDraft] = useState(DEFAULT_COMMANDER_NAME)
  const [inputFocused, setInputFocused] = useState(false)

  const inputRef = useRef<HTMLInputElement>(null)

  // Desktop autofocus only — on touch the soft keyboard would cover the reel.
  useEffect(() => {
    if (editing && window.matchMedia('(pointer: fine)').matches) {
      inputRef.current?.focus()
    }
  }, [editing])

  const handleDeploy = () => {
    play('button-press', 'ui')
    saveCommanderName(draft)
    onPlay()
  }

  const openEditor = (prefill: string) => {
    setDraft(prefill)
    setEditing(true)
  }

  return (
    <div
      style={{
        position: 'relative',
        minHeight: '100vh',
        overflow: 'hidden',
        backgroundColor: NAVY, // first paint / reel-loading fallback (§11.6)
      }}
    >
      {/* L1 — theater reel (decorative) */}
      <div aria-hidden style={{ position: 'absolute', inset: 0 }}>
        {reducedMotion ? (
          <img
            src={CLASSIC_SEGMENT.src}
            alt=""
            draggable={false}
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          REEL_SEGMENTS.map((seg, i) => (
            <img
              key={seg.id}
              src={seg.src}
              alt=""
              draggable={false}
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                opacity: 0,
                animation: `reel-fade ${CYCLE_S}s linear infinite, ${seg.drift} ${CYCLE_S}s linear infinite`,
                // Negative delay phase-shifts each segment into its 7s slot
                animationDelay: `${i * HOLD_S - CYCLE_S}s`,
              }}
            />
          ))
        )}
      </div>

      {/* L2 — bottom anchor gradient only (§11.1): the scene plays at full
          brightness; UI elements carry their own contrast (fly.pieter.com
          reference). Bottom 20% keeps the caption + CTA zone legible. */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          background: `linear-gradient(to bottom,
            rgba(11,37,69,0) 0%, rgba(11,37,69,0) 80%,
            rgba(11,37,69,0.45) 100%)`,
        }}
      />

      {/* Reel caption (§11.3) — decorative, synced to the reel */}
      <div aria-hidden style={{ position: 'absolute', left: 24, bottom: 24 }}>
        {(reducedMotion ? [CLASSIC_SEGMENT] : [...REEL_SEGMENTS]).map((seg, i) => (
          <div
            key={seg.id}
            style={{
              position: 'absolute',
              left: 0,
              bottom: 0,
              whiteSpace: 'nowrap',
              opacity: reducedMotion ? 1 : 0,
              animation: reducedMotion
                ? undefined
                : `reel-caption-fade ${CYCLE_S}s linear infinite`,
              animationDelay: reducedMotion ? undefined : `${i * HOLD_S - CYCLE_S}s`,
            }}
          >
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, color: 'rgba(234,242,248,0.85)' }}>
              {seg.caption}
            </div>
            <div style={{ width: 24, height: 2, backgroundColor: seg.accent, marginTop: 4 }} />
          </div>
        ))}
      </div>

      {/* L3 — split HUD layout (§11.4). Top group is always visible so the
          logo never disappears, even during first-visit name entry. */}
      <div
        style={{
          position: 'absolute',
          top: '6vh',
          left: 0,
          right: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 12,
          padding: '0 24px',
        }}
      >
        <img
          src={logo}
          alt="Salvo"
          draggable={false}
          style={{
            width: 'min(380px, 72vw)',
            filter: 'drop-shadow(0 2px 12px rgba(11,37,69,0.9)) drop-shadow(0 2px 12px rgba(11,37,69,0.9))',
          }}
        />
        <p
          style={{
            color: '#A6C6E1',
            fontSize: 14,
            textAlign: 'center',
            maxWidth: 300,
            textShadow: '0 1px 6px rgba(11,37,69,0.95), 0 1px 6px rgba(11,37,69,0.95)',
          }}
        >
          Place your fleet. Call your shots. Fire the salvo.
        </p>
      </div>

      {/* Bottom group — greeting + CTA, or the inline name-entry stack */}
      <div
        style={{
          position: 'absolute',
          bottom: '10vh',
          left: 0,
          right: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 16,
          padding: '0 24px',
        }}
      >
        {editing ? (
          <>
            <p style={chipStyle}>COMMANDER, IDENTIFY YOURSELF</p>
            <div style={{ width: '100%', maxWidth: 280 }}>
              <input
                ref={inputRef}
                type="text"
                value={draft}
                maxLength={MAX_COMMANDER_NAME_LENGTH}
                aria-label="Commander name"
                onChange={e => setDraft(e.target.value)}
                onFocus={e => {
                  setInputFocused(true)
                  e.currentTarget.select()
                }}
                onBlur={() => setInputFocused(false)}
                onKeyDown={e => {
                  if (e.key === 'Enter') handleDeploy()
                }}
                style={{
                  width: '100%',
                  height: 48,
                  padding: '0 16px',
                  fontSize: 16,
                  textAlign: 'center',
                  color: '#EAF2F8',
                  backgroundColor: 'rgba(11,37,69,0.92)',
                  borderRadius: 6,
                  outline: 'none',
                  // Focus reads as a 2px gold border without layout shift
                  border: inputFocused ? '1px solid #F4B942' : '1px solid rgba(141,169,196,0.4)',
                  boxShadow: inputFocused ? '0 0 0 1px #F4B942' : 'none',
                }}
              />
              {draft.length >= 12 && (
                <div style={{
                  fontSize: 10,
                  color: '#8DA9C4',
                  textAlign: 'right',
                  marginTop: 4,
                  textShadow: '0 1px 4px rgba(11,37,69,0.95)',
                }}>
                  {draft.length}/{MAX_COMMANDER_NAME_LENGTH}
                </div>
              )}
            </div>
            <button onClick={handleDeploy} style={primaryButtonStyle}>
              ▶ DEPLOY
            </button>
          </>
        ) : (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 16,
              width: '100%',
            }}
          >
            <div style={{ textAlign: 'center' }}>
              <p style={chipStyle}>
                WELCOME BACK, CMDR {(name ?? DEFAULT_COMMANDER_NAME).toUpperCase()}
              </p>
              <br />
              <button
                onClick={() => openEditor(name ?? DEFAULT_COMMANDER_NAME)}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  marginTop: 8,
                  fontSize: 11,
                  color: '#8DA9C4',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  textShadow: '0 1px 4px rgba(11,37,69,0.95)',
                }}
              >
                Not you? Change name
              </button>
            </div>

            <button
              onClick={() => {
                play('button-press', 'ui')
                onPlay()
              }}
              style={primaryButtonStyle}
            >
              ▶ BATTLE STATIONS
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
